import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { RootState } from "../../store";
import * as authSelectors from "../auth/authSelectors";
import * as userTermSelectors from "./userTermSelectors";
import * as userTermActions from "./userTermActions";
import { PageHeader } from "../common/PageHeader";
import { useForm } from "react-hook-form";
import { InputFormField } from "../common/hook-form/InputFormField";
import { routes } from "../../appRoutingHelpers";

interface Props {}

export const UserTermVersionUpsert: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const { id, userTermId } = useParams<{ id: string; userTermId: string }>();
  const isEdit = !!id;

  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const navigate = useNavigate();

  const userTermVersion = useSelector((state: RootState) =>
    userTermSelectors.userTermVersion(state, organizationKeyName, id!)
  );

  const isLoading = !userTermVersion;

  React.useEffect(() => {
    if (!id) {
      return;
    }

    dispatch(
      userTermActions.getUserTermVersionAction(
        organizationKeyName,
        userTermId!,
        id
      )
    );
  }, [dispatch, organizationKeyName, userTermId, id]);

  const formMethods = useForm();
  const { handleSubmit } = formMethods;

  const onSubmit = (data: any) => {
    if (id) {
      data.id = id;
    }

    data.userTermId = userTermId;

    dispatch(
      userTermActions.upsertUserTermVersionAction(organizationKeyName, data)
    );
    if (userTermId) {
      navigate(routes.userTermVersionList(organizationKeyName, userTermId));
    }
  };

  const isUpserting = false;

  if (isEdit && isLoading) {
    return null;
  }

  return (
    <div className="container">
      <PageHeader
        title="User term version"
        breadcrumbs={[
          { title: "User Term Version" },
          isEdit
            ? { title: "Edit", isActive: true }
            : { title: "New", isActive: true },
        ]}
      />

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                {isEdit ? "Edit" : "Create"} User term version
              </h3>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="card-body">
                <InputFormField
                  name="link"
                  label="Link"
                  errors={formMethods.formState.errors}
                  register={formMethods.register}
                  defaultValue={userTermVersion?.link}
                />
                <InputFormField
                  name="locale"
                  label="Locale"
                  errors={formMethods.formState.errors}
                  register={formMethods.register}
                  defaultValue={userTermVersion?.locale}
                />
                <InputFormField
                  name="version"
                  label="version"
                  errors={formMethods.formState.errors}
                  register={formMethods.register}
                  defaultValue={userTermVersion?.version}
                />
              </div>
              <div className="card-footer text-right">
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={isUpserting}
                >
                  {isUpserting ? <>Is saving</> : <>Save</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(UserTermVersionUpsert);
